import { useEffect, useState } from "react";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { Trash2, ArrowUp, ArrowDown, Upload, LogOut, Plus, Save, Film, Mail, KeyRound, FileText, BarChart3, Image as ImageIcon, Inbox } from "lucide-react";
import { parseGalleryItem } from "../lib/gallery";
import AdminTexts from "./AdminTexts";
import AdminAnalytics from "./AdminAnalytics";
import AdminMessages from "./AdminMessages";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "tico-admin-token";

const EMPTY = {
  key: "",
  client: "",
  title: "",
  year: "",
  role_en: "",
  role_pt: "",
  description_en: "",
  description_pt: "",
  image_url: "",
  video_url: "",
  span: "md:col-span-6",
  height: "h-[50vh] md:h-[58vh]",
  order: 999,
};

function useToken() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const save = (v) => {
    localStorage.setItem(TOKEN_KEY, v);
    setToken(v);
  };
  const clear = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
  };
  return [token, save, clear];
}

function Login({ onLogin }) {
  const [pwd, setPwd] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password: pwd });
      onLogin(data.token);
      toast.success("Welcome, Tico.");
    } catch {
      toast.error("Invalid password");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-[#0a0a0a] text-white">
      <form onSubmit={submit} className="w-full max-w-sm border border-[#262626] p-8">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#ff4500] mb-3">
          [ Admin ]
        </div>
        <h1 className="font-serif text-4xl font-light tracking-tight mb-8">Sign in</h1>
        <input
          data-testid="admin-password-input"
          type="password"
          value={pwd}
          onChange={(e) => setPwd(e.target.value)}
          placeholder="Password"
          autoFocus
          className="w-full bg-transparent border border-[#262626] px-4 py-3 mb-6 focus:border-[#ff4500] outline-none font-mono text-sm"
        />
        <button
          data-testid="admin-login-button"
          type="submit"
          disabled={busy}
          className="btn-orange w-full py-3 text-[11px] uppercase tracking-[0.25em] font-medium disabled:opacity-50"
        >
          <span>{busy ? "…" : "Enter"}</span>
        </button>
      </form>
    </div>
  );
}

function ProjectRow({ item, token, onChange, onDelete, onMove }) {
  const [p, setP] = useState(item);
  const [dirty, setDirty] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [urlInput, setUrlInput] = useState("");
  const set = (k, v) => {
    setP({ ...p, [k]: v });
    setDirty(true);
  };
  const persist = async (next) => {
    // Auto-save current row state (or provided state) to server
    const state = next || p;
    await axios.put(`${API}/projects/${p.id}`, state, { headers: { "X-Admin-Token": token } });
    setDirty(false);
    onChange();
  };
  const addUrl = async () => {
    const v = urlInput.trim();
    if (!v) return;
    const next = { ...p, gallery: [...(p.gallery || []), v] };
    setP(next);
    setUrlInput("");
    try {
      await persist(next);
      toast.success("URL added and saved");
    } catch {
      setDirty(true);
      toast.error("Save failed — click Save manually");
    }
  };
  const save = async () => {
    try {
      await persist(p);
      toast.success("Saved");
    } catch {
      toast.error("Save failed");
    }
  };
  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await axios.post(`${API}/projects/upload-image`, fd, {
        headers: { "X-Admin-Token": token, "Content-Type": "multipart/form-data" },
      });
      const next = { ...p, image_url: data.url };
      setP(next);
      await persist(next);
      toast.success(data.converted_from ? `Uploaded (${data.converted_from} → JPEG) and published` : "Image uploaded and published");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Upload failed";
      toast.error(msg);
    } finally {
      setUploading(false);
    }
  };
  const preview = p.image_url
    ? p.image_url.startsWith("http")
      ? p.image_url
      : `${process.env.REACT_APP_BACKEND_URL}${p.image_url}`
    : "";

  return (
    <div data-testid={`admin-project-${p.key}`} className="border border-[#262626] p-5 bg-[#0f0f0f] grid grid-cols-1 md:grid-cols-12 gap-4">
      <div className="md:col-span-2">
        {preview ? (
          <img src={preview} alt="" className="w-full aspect-square object-cover bg-[#1a1a1a]" />
        ) : (
          <div className="w-full aspect-square bg-[#1a1a1a] flex items-center justify-center text-neutral-600 font-mono text-xs">
            No image
          </div>
        )}
        <label className="mt-2 flex items-center justify-center gap-2 border border-[#262626] py-2 text-[10px] uppercase tracking-widest cursor-pointer hover:border-[#ff4500]">
          <Upload size={12} /> {uploading ? "…" : "Upload"}
          <input
            data-testid={`upload-input-${p.key}`}
            type="file"
            accept="image/*"
            onChange={(e) => upload(e.target.files?.[0])}
            className="hidden"
          />
        </label>
      </div>
      <div className="md:col-span-10 grid grid-cols-2 md:grid-cols-6 gap-3">
        {[
          ["key", "Key", 1],
          ["client", "Client", 1],
          ["title", "Title", 2],
          ["year", "Year", 1],
          ["order", "Order", 1],
          ["role_en", "Role (EN)", 3],
          ["role_pt", "Role (PT)", 3],
          ["image_url", "Image URL / path", 3],
          ["video_url", "Video URL (mp4/webm)", 3],
          ["span", "Grid span", 3],
          ["height", "Height class", 3],
        ].map(([field, label, span]) => (
          <label key={field} className={`md:col-span-${span} col-span-2 flex flex-col`}>
            <span className="font-mono text-[9px] uppercase tracking-widest text-neutral-500 mb-1">{label}</span>
            <input
              value={p[field] ?? ""}
              onChange={(e) => set(field, field === "order" ? Number(e.target.value) : e.target.value)}
              className="bg-transparent border border-[#262626] px-2 py-1.5 text-sm focus:border-[#ff4500] outline-none"
            />
          </label>
        ))}
        {[
          ["description_en", "Description (EN) — short case description shown on hover"],
          ["description_pt", "Description (PT) — descrição curta mostrada no hover"],
          ["long_description_en", "Long description (EN) — full case study body, separate paragraphs with a blank line"],
          ["long_description_pt", "Long description (PT) — corpo do case study, parágrafos separados por linha em branco"],
        ].map(([field, label]) => (
          <label key={field} className="col-span-2 md:col-span-6 flex flex-col">
            <span className="font-mono text-[9px] uppercase tracking-widest text-neutral-500 mb-1">{label}</span>
            <textarea
              value={p[field] ?? ""}
              onChange={(e) => set(field, e.target.value)}
              rows={field.startsWith("long_") ? 5 : 2}
              className="bg-transparent border border-[#262626] px-2 py-1.5 text-sm focus:border-[#ff4500] outline-none resize-y"
            />
          </label>
        ))}

        {/* Gallery editor */}
        <div className="col-span-2 md:col-span-6">
          <div className="font-mono text-[9px] uppercase tracking-widest text-neutral-500 mb-2">
            Gallery ({(p.gallery || []).length} items) — images or video links (Vimeo, YouTube, .mp4/.webm)
          </div>
          <div className="flex flex-wrap gap-3">
            {(p.gallery || []).map((g, gi) => {
              const parsed = parseGalleryItem(g);
              const isImg = parsed.type === "image";
              const thumb = parsed.thumbUrl || (isImg ? (g?.startsWith("http") ? g : `${process.env.REACT_APP_BACKEND_URL}${g}`) : "");
              return (
                <div key={gi} className="relative w-24 h-24 group border border-[#262626] bg-[#1a1a1a]">
                  {thumb ? (
                    <img src={thumb} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-neutral-600 text-xs font-mono uppercase">
                      {parsed.type}
                    </div>
                  )}
                  {parsed.type !== "image" && (
                    <span className="absolute bottom-1 left-1 font-mono text-[8px] uppercase tracking-widest bg-black/70 text-white px-1.5 py-0.5">
                      {parsed.type}
                    </span>
                  )}
                  <button
                    type="button"
                    data-testid={`gallery-remove-${p.key}-${gi}`}
                    onClick={async () => {
                    const next = { ...p, gallery: (p.gallery || []).filter((_, idx) => idx !== gi) };
                    setP(next);
                    try { await persist(next); toast.success("Removed and saved"); }
                    catch { setDirty(true); toast.error("Save failed"); }
                  }}
                    className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-black border border-white/20 hover:border-[#ff4500] hover:bg-[#ff4500] text-white hover:text-black text-xs flex items-center justify-center transition-all opacity-0 group-hover:opacity-100"
                    aria-label="Remove"
                  >
                    ×
                  </button>
                </div>
              );
            })}
            <label
              data-testid={`gallery-add-${p.key}`}
              className="w-24 h-24 border border-dashed border-[#262626] hover:border-[#ff4500] flex flex-col items-center justify-center gap-1 cursor-pointer text-neutral-500 hover:text-[#ff4500] text-[10px] uppercase tracking-widest"
            >
              <Upload size={14} />
              <span>{uploading ? "…" : "Upload"}</span>
              <input
                type="file"
                accept="image/*"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  setUploading(true);
                  const fd = new FormData();
                  fd.append("file", file);
                  try {
                    const { data } = await axios.post(`${API}/projects/upload-image`, fd, {
                      headers: { "X-Admin-Token": token, "Content-Type": "multipart/form-data" },
                    });
                    const next = { ...p, gallery: [...(p.gallery || []), data.url] };
                    setP(next);
                    await persist(next);
                    toast.success(data.converted_from ? `Added (${data.converted_from} → JPEG) and published` : "Added to gallery and published");
                  } catch (err) {
                    toast.error(err?.response?.data?.detail || "Upload failed");
                  } finally {
                    setUploading(false);
                  }
                  e.target.value = "";
                }}
                className="hidden"
              />
            </label>
          </div>
          <div className="mt-3 flex flex-col sm:flex-row gap-2">
            <input
              data-testid={`gallery-url-input-${p.key}`}
              type="url"
              placeholder="Paste Vimeo, YouTube or image/mp4 URL"
              value={urlInput}
              onChange={(e) => setUrlInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addUrl();
                }
              }}
              className="flex-1 bg-transparent border border-[#262626] px-3 py-1.5 text-sm focus:border-[#ff4500] outline-none"
            />
            <button
              type="button"
              data-testid={`gallery-url-add-${p.key}`}
              onClick={addUrl}
              disabled={!urlInput.trim()}
              className={`inline-flex items-center gap-2 px-4 py-1.5 text-[10px] uppercase tracking-widest font-medium ${urlInput.trim() ? "bg-[#ff4500] text-black" : "bg-[#1a1a1a] text-neutral-600 cursor-not-allowed"}`}
            >
              <Plus size={12} /> Add URL
            </button>
          </div>
        </div>
        <div className="col-span-2 md:col-span-6 flex items-center justify-between gap-2 mt-2">
          <div className="flex items-center gap-2">
            <button onClick={() => onMove(p, -1)} className="p-2 border border-[#262626] hover:border-[#ff4500]" title="Move up"><ArrowUp size={14} /></button>
            <button onClick={() => onMove(p, 1)} className="p-2 border border-[#262626] hover:border-[#ff4500]" title="Move down"><ArrowDown size={14} /></button>
            <button
              data-testid={`delete-project-${p.key}`}
              onClick={() => { if (confirm(`Delete ${p.title}?`)) onDelete(p.id); }}
              className="p-2 border border-[#262626] hover:border-red-500 text-red-500"
            >
              <Trash2 size={14} />
            </button>
          </div>
          <button
            data-testid={`save-project-${p.key}`}
            onClick={save}
            disabled={!dirty}
            className={`inline-flex items-center gap-2 px-4 py-2 text-[10px] uppercase tracking-widest font-medium ${dirty ? "bg-[#ff4500] text-black" : "bg-[#1a1a1a] text-neutral-600 cursor-not-allowed"}`}
          >
            <Save size={12} /> {dirty ? "Save" : "Saved"}
          </button>
        </div>
      </div>
    </div>
  );
}

function AddNew({ token, onCreated }) {
  const [open, setOpen] = useState(false);
  const [p, setP] = useState(EMPTY);
  const create = async () => {
    if (!p.key || !p.client || !p.title) {
      toast.error("Key, Client and Title required");
      return;
    }
    try {
      await axios.post(`${API}/projects`, p, { headers: { "X-Admin-Token": token } });
      toast.success("Project created");
      setP(EMPTY);
      setOpen(false);
      onCreated();
    } catch {
      toast.error("Create failed");
    }
  };
  if (!open)
    return (
      <button
        data-testid="add-new-project-button"
        onClick={() => setOpen(true)}
        className="w-full py-6 border border-dashed border-[#262626] hover:border-[#ff4500] text-[11px] uppercase tracking-widest text-neutral-500 hover:text-[#ff4500] flex items-center justify-center gap-2"
      >
        <Plus size={14} /> Add new project
      </button>
    );
  return (
    <div className="border border-[#ff4500] p-5 bg-[#0f0f0f] grid grid-cols-2 md:grid-cols-6 gap-3">
      {["key","client","title","year","role_en","role_pt","image_url","video_url"].map((f) => (
        <label key={f} className="col-span-2 md:col-span-3 flex flex-col">
          <span className="font-mono text-[9px] uppercase tracking-widest text-neutral-500 mb-1">{f}</span>
          <input
            value={p[f]}
            onChange={(e) => setP({ ...p, [f]: e.target.value })}
            className="bg-transparent border border-[#262626] px-2 py-1.5 text-sm focus:border-[#ff4500] outline-none"
          />
        </label>
      ))}
      {["description_en","description_pt"].map((f) => (
        <label key={f} className="col-span-2 md:col-span-6 flex flex-col">
          <span className="font-mono text-[9px] uppercase tracking-widest text-neutral-500 mb-1">{f}</span>
          <textarea
            value={p[f]}
            onChange={(e) => setP({ ...p, [f]: e.target.value })}
            rows={2}
            className="bg-transparent border border-[#262626] px-2 py-1.5 text-sm focus:border-[#ff4500] outline-none resize-y"
          />
        </label>
      ))}
      <div className="col-span-2 md:col-span-6 flex justify-end gap-2 mt-2">
        <button onClick={() => { setOpen(false); setP(EMPTY); }} className="px-4 py-2 text-[10px] uppercase tracking-widest border border-[#262626]">Cancel</button>
        <button data-testid="create-project-button" onClick={create} className="btn-orange px-4 py-2 text-[10px] uppercase tracking-widest font-medium"><span>Create</span></button>
      </div>
    </div>
  );
}

function HeroReelPanel({ token }) {
  const [s, setS] = useState({
    hero_reel_url: "",
    hero_reel_poster: "",
    hero_reel_caption: "",
    hero_bg_video_url: "",
    hero_bg_video_poster: "",
    hero_bg_video_enabled: false,
  });
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/settings`).then(({ data }) => setS(data));
  }, []);

  const set = (k, v) => {
    setS({ ...s, [k]: v });
    setDirty(true);
  };

  const upload = async (file, field) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await axios.post(`${API}/projects/upload-image`, fd, {
        headers: { "X-Admin-Token": token, "Content-Type": "multipart/form-data" },
      });
      const next = { ...s, [field]: data.url };
      setS(next);
      // Auto-save so the reel appears on the site immediately
      await axios.put(
        `${API}/settings`,
        { [field]: data.url },
        { headers: { "X-Admin-Token": token } }
      );
      setDirty(false);
      toast.success(data.converted_from ? `Uploaded (${data.converted_from} → JPEG) and published` : "Uploaded and published");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    try {
      await axios.put(`${API}/settings`, s, { headers: { "X-Admin-Token": token } });
      setDirty(false);
      toast.success("Hero reel updated");
    } catch {
      toast.error("Save failed");
    }
  };

  const preview = s.hero_reel_url
    ? s.hero_reel_url.startsWith("http")
      ? s.hero_reel_url
      : `${process.env.REACT_APP_BACKEND_URL}${s.hero_reel_url}`
    : "";
  const posterPreview = s.hero_reel_poster
    ? s.hero_reel_poster.startsWith("http")
      ? s.hero_reel_poster
      : `${process.env.REACT_APP_BACKEND_URL}${s.hero_reel_poster}`
    : "";

  return (
    <div data-testid="admin-hero-reel-panel" className="border border-[#ff4500]/40 bg-[#0f0f0f] p-5 mb-6">
      <div className="flex items-center gap-3 mb-5">
        <Film size={16} className="text-[#ff4500]" />
        <h2 className="font-serif text-2xl font-light">Hero Reel</h2>
        <span className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">— featured video on homepage</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
        <div className="md:col-span-3">
          {preview ? (
            <video src={preview} poster={posterPreview} autoPlay muted loop playsInline className="w-full aspect-[4/5] object-cover bg-black" />
          ) : (
            <div className="w-full aspect-[4/5] bg-[#1a1a1a] flex items-center justify-center text-neutral-600 font-mono text-[10px] uppercase tracking-widest">No reel yet</div>
          )}
          <label className="mt-2 flex items-center justify-center gap-2 border border-[#262626] py-2 text-[10px] uppercase tracking-widest cursor-pointer hover:border-[#ff4500]">
            <Upload size={12} /> {busy ? "…" : "Upload video"}
            <input
              data-testid="hero-reel-upload"
              type="file"
              accept="video/mp4,video/webm,video/quicktime"
              onChange={(e) => upload(e.target.files?.[0], "hero_reel_url")}
              className="hidden"
            />
          </label>
        </div>
        <div className="md:col-span-9 grid grid-cols-2 md:grid-cols-6 gap-3">
          <label className="col-span-2 md:col-span-6 flex flex-col">
            <span className="font-mono text-[9px] uppercase tracking-widest text-neutral-500 mb-1">Reel video URL (mp4 / webm)</span>
            <input value={s.hero_reel_url ?? ""} onChange={(e) => set("hero_reel_url", e.target.value)} placeholder="https://... or /api/uploads/xyz.mp4" className="bg-transparent border border-[#262626] px-2 py-1.5 text-sm focus:border-[#ff4500] outline-none" />
          </label>
          <label className="col-span-2 md:col-span-4 flex flex-col">
            <span className="font-mono text-[9px] uppercase tracking-widest text-neutral-500 mb-1">Poster image URL (fallback)</span>
            <input value={s.hero_reel_poster ?? ""} onChange={(e) => set("hero_reel_poster", e.target.value)} placeholder="https://... or /api/uploads/xyz.jpg" className="bg-transparent border border-[#262626] px-2 py-1.5 text-sm focus:border-[#ff4500] outline-none" />
          </label>
          <label className="col-span-2 md:col-span-2 flex flex-col">
            <span className="font-mono text-[9px] uppercase tracking-widest text-neutral-500 mb-1">Caption</span>
            <input value={s.hero_reel_caption ?? ""} onChange={(e) => set("hero_reel_caption", e.target.value)} placeholder="Reel · 2026" className="bg-transparent border border-[#262626] px-2 py-1.5 text-sm focus:border-[#ff4500] outline-none" />
          </label>
          <label className="col-span-2 md:col-span-6 mt-1 flex items-center justify-center gap-2 border border-[#262626] py-2 text-[10px] uppercase tracking-widest cursor-pointer hover:border-[#ff4500]">
            <Upload size={12} /> Upload poster image
            <input type="file" accept="image/*" onChange={(e) => upload(e.target.files?.[0], "hero_reel_poster")} className="hidden" />
          </label>
          <div className="col-span-2 md:col-span-6 flex justify-between items-center mt-2">
            <p className="font-mono text-[10px] text-neutral-500">
              Leave URL blank to hide reel and use original hero layout.
            </p>
            <button
              data-testid="hero-reel-save"
              onClick={save}
              disabled={!dirty}
              className={`inline-flex items-center gap-2 px-5 py-2 text-[10px] uppercase tracking-widest font-medium ${dirty ? "bg-[#ff4500] text-black" : "bg-[#1a1a1a] text-neutral-600 cursor-not-allowed"}`}
            >
              <Save size={12} /> {dirty ? "Save reel" : "Saved"}
            </button>
          </div>
        </div>
      </div>

      {/* Background showreel */}
      <div className="border-t border-[#1a1a1a] mt-8 pt-6">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <Film size={14} className="text-[#ff4500]" />
            <h3 className="font-serif text-xl font-light">Background Showreel</h3>
            <span className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">— subtle video behind the hero title</span>
          </div>
          <button
            type="button"
            data-testid="hero-bg-toggle"
            onClick={async () => {
              const next = !s.hero_bg_video_enabled;
              setS({ ...s, hero_bg_video_enabled: next });
              try {
                await axios.put(`${API}/settings`, { hero_bg_video_enabled: next }, { headers: { "X-Admin-Token": token } });
                toast.success(next ? "Background video enabled" : "Background video disabled");
              } catch {
                toast.error("Save failed");
              }
            }}
            className={`px-5 py-2 font-mono text-[10px] uppercase tracking-widest border transition-all ${
              s.hero_bg_video_enabled
                ? "bg-[#ff4500] text-black border-[#ff4500]"
                : "border-[#262626] text-neutral-400 hover:border-[#ff4500]"
            }`}
          >
            {s.hero_bg_video_enabled ? "Enabled" : "Disabled"}
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
          <div className="md:col-span-3">
            {s.hero_bg_video_url ? (
              <video
                src={s.hero_bg_video_url.startsWith("http") ? s.hero_bg_video_url : `${process.env.REACT_APP_BACKEND_URL}${s.hero_bg_video_url}`}
                autoPlay muted loop playsInline
                className="w-full aspect-video object-cover bg-black"
              />
            ) : (
              <div className="w-full aspect-video bg-[#1a1a1a] flex items-center justify-center text-neutral-600 font-mono text-[10px] uppercase tracking-widest">No video yet</div>
            )}
            <label className="mt-2 flex items-center justify-center gap-2 border border-[#262626] py-2 text-[10px] uppercase tracking-widest cursor-pointer hover:border-[#ff4500]">
              <Upload size={12} /> {busy ? "…" : "Upload video"}
              <input
                data-testid="hero-bg-upload"
                type="file"
                accept="video/mp4,video/webm,video/quicktime"
                onChange={(e) => upload(e.target.files?.[0], "hero_bg_video_url")}
                className="hidden"
              />
            </label>
          </div>
          <div className="md:col-span-9 grid grid-cols-2 md:grid-cols-6 gap-3">
            <label className="col-span-2 md:col-span-6 flex flex-col">
              <span className="font-mono text-[9px] uppercase tracking-widest text-neutral-500 mb-1">Background video URL (mp4 / webm — short loop, compressed, ~5MB max recommended)</span>
              <input data-testid="hero-bg-video-url" value={s.hero_bg_video_url ?? ""} onChange={(e) => set("hero_bg_video_url", e.target.value)} placeholder="https://... or /api/uploads/xyz.mp4" className="bg-transparent border border-[#262626] px-2 py-1.5 text-sm focus:border-[#ff4500] outline-none" />
            </label>
            <label className="col-span-2 md:col-span-4 flex flex-col">
              <span className="font-mono text-[9px] uppercase tracking-widest text-neutral-500 mb-1">Poster / fallback image URL</span>
              <input data-testid="hero-bg-poster-url" value={s.hero_bg_video_poster ?? ""} onChange={(e) => set("hero_bg_video_poster", e.target.value)} placeholder="https://... or /api/uploads/xyz.jpg" className="bg-transparent border border-[#262626] px-2 py-1.5 text-sm focus:border-[#ff4500] outline-none" />
            </label>
            <label className="col-span-2 md:col-span-2 flex items-center justify-center gap-2 border border-[#262626] py-2 text-[10px] uppercase tracking-widest cursor-pointer hover:border-[#ff4500] self-end">
              <Upload size={12} /> Upload poster
              <input type="file" accept="image/*" onChange={(e) => upload(e.target.files?.[0], "hero_bg_video_poster")} className="hidden" />
            </label>
          </div>
        </div>
      </div>
    </div>
  );
}

function ContactPanel({ token }) {
  const [s, setS] = useState({
    contact_email: "",
    linkedin_url: "",
    behance_url: "",
    instagram_url: "",
    availability_en: "",
    availability_pt: "",
  });
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    axios.get(`${API}/settings`).then(({ data }) => setS((prev) => ({ ...prev, ...data })));
  }, []);

  const set = (k, v) => {
    setS({ ...s, [k]: v });
    setDirty(true);
  };

  const save = async () => {
    try {
      await axios.put(
        `${API}/settings`,
        {
          contact_email: s.contact_email,
          linkedin_url: s.linkedin_url,
          behance_url: s.behance_url,
          instagram_url: s.instagram_url,
          availability_en: s.availability_en,
          availability_pt: s.availability_pt,
        },
        { headers: { "X-Admin-Token": token } }
      );
      setDirty(false);
      toast.success("Contact settings saved");
    } catch {
      toast.error("Save failed");
    }
  };

  const fields = [
    ["contact_email", "Contact email (used in mailto)", "tico@studionuts.com.br", 6],
    ["linkedin_url", "LinkedIn URL", "https://linkedin.com/in/...", 3],
    ["behance_url", "Behance URL", "https://behance.net/...", 3],
    ["instagram_url", "Instagram URL", "https://instagram.com/...", 3],
  ];

  return (
    <div data-testid="admin-contact-panel" className="border border-[#262626] bg-[#0f0f0f] p-5 mb-6">
      <div className="flex items-center gap-3 mb-5">
        <Mail size={16} className="text-[#ff4500]" />
        <h2 className="font-serif text-2xl font-light">Contact & Links</h2>
        <span className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">— editable email and socials</span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        {fields.map(([f, label, ph, span]) => (
          <label key={f} className={`col-span-2 md:col-span-${span} flex flex-col`}>
            <span className="font-mono text-[9px] uppercase tracking-widest text-neutral-500 mb-1">{label}</span>
            <input
              data-testid={`contact-${f}`}
              value={s[f] ?? ""}
              onChange={(e) => set(f, e.target.value)}
              placeholder={ph}
              className="bg-transparent border border-[#262626] px-2 py-1.5 text-sm focus:border-[#ff4500] outline-none"
            />
          </label>
        ))}
        <label className="col-span-2 md:col-span-3 flex flex-col">
          <span className="font-mono text-[9px] uppercase tracking-widest text-neutral-500 mb-1">Availability (EN)</span>
          <textarea rows={2} value={s.availability_en ?? ""} onChange={(e) => set("availability_en", e.target.value)} className="bg-transparent border border-[#262626] px-2 py-1.5 text-sm focus:border-[#ff4500] outline-none resize-y" />
        </label>
        <label className="col-span-2 md:col-span-3 flex flex-col">
          <span className="font-mono text-[9px] uppercase tracking-widest text-neutral-500 mb-1">Availability (PT)</span>
          <textarea rows={2} value={s.availability_pt ?? ""} onChange={(e) => set("availability_pt", e.target.value)} className="bg-transparent border border-[#262626] px-2 py-1.5 text-sm focus:border-[#ff4500] outline-none resize-y" />
        </label>
        <div className="col-span-2 md:col-span-6 flex justify-end mt-2">
          <button
            data-testid="contact-save"
            onClick={save}
            disabled={!dirty}
            className={`inline-flex items-center gap-2 px-5 py-2 text-[10px] uppercase tracking-widest font-medium ${dirty ? "bg-[#ff4500] text-black" : "bg-[#1a1a1a] text-neutral-600 cursor-not-allowed"}`}
          >
            <Save size={12} /> {dirty ? "Save contact" : "Saved"}
          </button>
        </div>
      </div>
    </div>
  );
}

function AboutPhotoPanel({ token }) {
  const [url, setUrl] = useState("");
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/settings`).then(({ data }) => setUrl(data?.about_portrait_url || ""));
  }, []);

  const publish = async (value) => {
    await axios.put(`${API}/settings`, { about_portrait_url: value }, { headers: { "X-Admin-Token": token } });
    setDirty(false);
  };

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await axios.post(`${API}/projects/upload-image`, fd, {
        headers: { "X-Admin-Token": token, "Content-Type": "multipart/form-data" },
      });
      setUrl(data.url);
      await publish(data.url);
      toast.success(data.converted_from ? `Uploaded (${data.converted_from} → JPEG) and published` : "Portrait uploaded and published");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    try {
      await publish(url);
      toast.success("About photo updated");
    } catch {
      toast.error("Save failed");
    }
  };

  const preview = url
    ? url.startsWith("http")
      ? url
      : `${process.env.REACT_APP_BACKEND_URL}${url}`
    : "";

  return (
    <div data-testid="admin-about-photo-panel" className="border border-[#262626] bg-[#0f0f0f] p-5 mb-6">
      <div className="flex items-center gap-3 mb-5">
        <ImageIcon size={16} className="text-[#ff4500]" />
        <h2 className="font-serif text-2xl font-light">About Photo</h2>
        <span className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">— portrait in the About section</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
        <div className="md:col-span-2">
          {preview ? (
            <img src={preview} alt="" className="w-full aspect-[3/4] object-cover bg-[#1a1a1a]" />
          ) : (
            <div className="w-full aspect-[3/4] bg-[#1a1a1a] flex items-center justify-center text-neutral-600 font-mono text-[10px] uppercase tracking-widest">Default</div>
          )}
          <label className="mt-2 flex items-center justify-center gap-2 border border-[#262626] py-2 text-[10px] uppercase tracking-widest cursor-pointer hover:border-[#ff4500]">
            <Upload size={12} /> {busy ? "…" : "Upload"}
            <input
              data-testid="about-photo-upload"
              type="file"
              accept="image/*"
              onChange={(e) => upload(e.target.files?.[0])}
              className="hidden"
            />
          </label>
        </div>
        <div className="md:col-span-10 flex flex-col gap-3">
          <label className="flex flex-col">
            <span className="font-mono text-[9px] uppercase tracking-widest text-neutral-500 mb-1">Portrait image URL (blank = original photo)</span>
            <input
              data-testid="about-photo-url"
              value={url}
              onChange={(e) => { setUrl(e.target.value); setDirty(true); }}
              placeholder="https://... or /api/uploads/xyz.jpg"
              className="bg-transparent border border-[#262626] px-2 py-1.5 text-sm focus:border-[#ff4500] outline-none"
            />
          </label>
          <div className="flex justify-end">
            <button
              data-testid="about-photo-save"
              onClick={save}
              disabled={!dirty}
              className={`inline-flex items-center gap-2 px-5 py-2 text-[10px] uppercase tracking-widest font-medium ${dirty ? "bg-[#ff4500] text-black" : "bg-[#1a1a1a] text-neutral-600 cursor-not-allowed"}`}
            >
              <Save size={12} /> {dirty ? "Save photo" : "Saved"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ChangePasswordModal({ open, onClose, token }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 6) return toast.error("New password must be at least 6 characters");
    if (next !== confirm) return toast.error("Passwords don't match");
    setBusy(true);
    try {
      await axios.post(
        `${API}/admin/change-password`,
        { current_password: current, new_password: next },
        { headers: { "X-Admin-Token": token } }
      );
      toast.success("Password updated");
      setCurrent(""); setNext(""); setConfirm("");
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Change failed");
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-md flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="w-full max-w-md border border-[#ff4500]/40 bg-[#0a0a0a] p-8">
        <div className="flex items-center gap-2 mb-6">
          <KeyRound size={16} className="text-[#ff4500]" />
          <h2 className="font-serif text-3xl font-light">Change password</h2>
        </div>
        {[
          ["Current password", current, setCurrent, "current"],
          ["New password (min 6 chars)", next, setNext, "new"],
          ["Confirm new password", confirm, setConfirm, "confirm"],
        ].map(([label, val, setter, key]) => (
          <label key={key} className="block mb-4">
            <span className="block font-mono text-[9px] uppercase tracking-widest text-neutral-500 mb-1">{label}</span>
            <input
              data-testid={`password-${key}`}
              type="password"
              value={val}
              onChange={(e) => setter(e.target.value)}
              className="w-full bg-transparent border border-[#262626] px-3 py-2 focus:border-[#ff4500] outline-none font-mono text-sm"
              required
            />
          </label>
        ))}
        <div className="flex gap-2 justify-end mt-6">
          <button type="button" onClick={onClose} className="px-4 py-2 text-[10px] uppercase tracking-widest border border-[#262626]">Cancel</button>
          <button data-testid="password-submit" type="submit" disabled={busy} className="btn-orange px-5 py-2 text-[10px] uppercase tracking-widest font-medium disabled:opacity-50">
            <span>{busy ? "…" : "Update"}</span>
          </button>
        </div>
      </form>
    </div>
  );
}

export default function AdminApp() {
  const [token, saveToken, clearToken] = useToken();
  const [items, setItems] = useState([]);
  const [pwdOpen, setPwdOpen] = useState(false);
  const [tab, setTab] = useState("manage");

  const load = async () => {
    const { data } = await axios.get(`${API}/projects`);
    setItems(data);
  };

  useEffect(() => { if (token) load(); }, [token]);

  const del = async (id) => {
    try {
      await axios.delete(`${API}/projects/${id}`, { headers: { "X-Admin-Token": token } });
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const move = async (p, dir) => {
    const sorted = [...items].sort((a, b) => a.order - b.order);
    const idx = sorted.findIndex((x) => x.id === p.id);
    const swap = sorted[idx + dir];
    if (!swap) return;
    try {
      await Promise.all([
        axios.put(`${API}/projects/${p.id}`, { order: swap.order }, { headers: { "X-Admin-Token": token } }),
        axios.put(`${API}/projects/${swap.id}`, { order: p.order }, { headers: { "X-Admin-Token": token } }),
      ]);
      load();
    } catch {
      toast.error("Reorder failed");
    }
  };

  if (!token) return (<><Toaster theme="dark" /><Login onLogin={saveToken} /></>);

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white pb-24">
      <Toaster theme="dark" position="bottom-right" />
      <ChangePasswordModal open={pwdOpen} onClose={() => setPwdOpen(false)} token={token} />
      <header className="border-b border-[#1a1a1a] px-6 md:px-12 py-6 flex items-center justify-between sticky top-0 bg-[#0a0a0a]/95 backdrop-blur z-10">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#ff4500]">[ Admin ]</div>
          <h1 className="font-serif text-3xl font-light">Studio CMS</h1>
        </div>
        <div className="flex items-center gap-3">
          <a href="/" className="text-[11px] uppercase tracking-widest text-neutral-400 hover:text-white hidden sm:inline">View site ↗</a>
          <button
            data-testid="change-password-button"
            onClick={() => setPwdOpen(true)}
            className="inline-flex items-center gap-2 px-4 py-2 border border-[#262626] hover:border-[#ff4500] text-[11px] uppercase tracking-widest"
          >
            <KeyRound size={13} /> Password
          </button>
          <button data-testid="admin-logout-button" onClick={clearToken} className="inline-flex items-center gap-2 px-4 py-2 border border-[#262626] hover:border-[#ff4500] text-[11px] uppercase tracking-widest">
            <LogOut size={13} /> Logout
          </button>
        </div>
      </header>

      <main className="px-6 md:px-12 py-8 max-w-[1400px] mx-auto">
        <div className="flex gap-2 mb-8 border-b border-[#1a1a1a] pb-4">
          {[
            ["manage", "Projects & Settings", ImageIcon],
            ["texts", "Site Texts", FileText],
            ["analytics", "Analytics", BarChart3],
            ["messages", "Messages", Inbox],
          ].map(([id, label, Icon]) => (
            <button
              key={id}
              data-testid={`admin-tab-${id}`}
              onClick={() => setTab(id)}
              className={`inline-flex items-center gap-2 px-5 py-2.5 text-[11px] uppercase tracking-widest font-medium border transition-all ${
                tab === id
                  ? "bg-[#ff4500] text-black border-[#ff4500]"
                  : "border-[#262626] text-neutral-400 hover:border-[#ff4500] hover:text-white"
              }`}
            >
              <Icon size={13} /> {label}
            </button>
          ))}
        </div>

        {tab === "texts" && <AdminTexts token={token} />}
        {tab === "analytics" && <AdminAnalytics token={token} />}
        {tab === "messages" && <AdminMessages token={token} />}

        {tab === "manage" && (
          <>
        <HeroReelPanel token={token} />
        <AboutPhotoPanel token={token} />
        <ContactPanel token={token} />
        <div className="border-t border-[#1a1a1a] pt-6 mt-6 mb-4">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#ff4500] mb-2">[ Work ]</div>
          <h2 className="font-serif text-2xl font-light">Selected Work</h2>
        </div>
        <div className="space-y-4">
          <AddNew token={token} onCreated={load} />
          {items
            .sort((a, b) => a.order - b.order)
            .map((it) => (
              <ProjectRow key={it.id} item={it} token={token} onChange={load} onDelete={del} onMove={move} />
            ))}
        </div>
          </>
        )}
      </main>
    </div>
  );
}
